"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface CoreFeature {
  number: string;
  title: string;
  body: string;
  icon?: LucideIcon;
}

export interface CoreFeaturesSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  features: CoreFeature[];
  accent: string;
  featureAccents: string[];
  gridCols?: 2 | 3 | 4;
}

export function CoreFeaturesSection({
  id = "core-features",
  eyebrow,
  title,
  features,
  accent,
  featureAccents,
  gridCols = 2,
}: CoreFeaturesSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className={`mt-12 grid gap-5 ${gridCols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : gridCols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2"}`}>
          {features.map((f, i) => {
            const fAccent = featureAccents[i % featureAccents.length] ?? accent;
            return (
              <Reveal key={f.number} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${fAccent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {f.icon ? (
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, ${fAccent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${fAccent} 35%, transparent)`,
                        }}
                      >
                        <f.icon className="h-4 w-4" style={{ color: fAccent }} />
                      </span>
                    ) : (
                      <span
                        className="grid place-items-center h-8 w-8 rounded-lg text-[11px] font-bold"
                        style={{
                          background: `color-mix(in oklch, ${fAccent} 18%, transparent)`,
                          color: fAccent,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${fAccent} 35%, transparent)`,
                        }}
                      >
                        {f.number}
                      </span>
                    )}
                    <span className="eyebrow-tiny font-bold" style={{ color: fAccent }}>
                      Feature
                    </span>
                  </div>
                  <h3 className="text-base">{f.title}</h3>
                  <p className="mt-3 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
